import {observer} from 'mobx-react';
import {IconButton, Slider, Typography} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import {Frame} from '../common/Frame';
import {VideoPlayerState, formatTime} from './useVideoPlayerState';

interface VideoPlayerControlsProps {
  state: VideoPlayerState;
  onPlayPause: () => void;
  onReplay?: () => void;
  onVolumeChange: (volume: number) => void;
  onMuteToggle: () => void;
  onFullscreen: () => void;
  selectedAnnotationTitle?: string;
  onAnnotationClose?: () => void;
}

export const VideoPlayerControls = observer(({
  state,
  onPlayPause,
  onReplay,
  onVolumeChange,
  onMuteToggle,
  onFullscreen,
  selectedAnnotationTitle,
  onAnnotationClose,
}: VideoPlayerControlsProps) => {
  return (
    <Frame row alignItems="center" gap={1}>
      {/* Replay */}
      {onReplay && (
        <IconButton
          onClick={onReplay}
          size="small"
          sx={{color: 'white', marginRight: -0.5}}
        >
          <ReplayIcon />
        </IconButton>
      )}

      {/* Play/Pause */}
      <IconButton
        onClick={onPlayPause}
        size="small"
        sx={{color: 'white'}}
      >
        {state.playing ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>

      {/* Time Display */}
      <Typography variant="caption" sx={{color: 'white', minWidth: 80, fontFamily: 'monospace'}}>
        {formatTime(state.currentTimeMs)} / {formatTime(state.durationMs)}
      </Typography>

      {/* Spacer */}
      <Frame grow />

      {/* Selected Annotation Title */}
      {onAnnotationClose && selectedAnnotationTitle && (
        <Frame
          row
          alignItems="center"
          gap={0.5}
          paddingH={1}
          style={{
            maxWidth: '40%',
            border: '1px solid rgba(255,255,255,0.5)',
            borderRadius: 16,
            height: 28,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'white',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedAnnotationTitle}
          </Typography>
          <IconButton
            onClick={onAnnotationClose}
            size="small"
            sx={{color: 'white', padding: 0.25, marginRight: -0.5}}
          >
            <CloseIcon sx={{fontSize: 16}} />
          </IconButton>
        </Frame>
      )}

      {/* Spacer */}
      <Frame grow />

      {/* Volume */}
      <Frame row alignItems="center" gap={0.5} style={{width: 120}}>
        <IconButton
          onClick={onMuteToggle}
          size="small"
          sx={{color: 'white', zIndex: 1}}
        >
          {state.muted || state.volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
        <Slider
          value={state.muted ? 0 : state.volume}
          onChange={(_, value) => onVolumeChange(value as number)}
          min={0}
          max={1}
          step={0.1}
          size="small"
          sx={{
            color: 'white',
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
            },
          }}
        />
      </Frame>

      {/* Fullscreen */}
      <IconButton
        onClick={onFullscreen}
        size="small"
        sx={{color: 'white'}}
      >
        <FullscreenIcon />
      </IconButton>
    </Frame>
  );
});
